/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  CUSTOMER_ROLES,
  type CustomerRole,
  ROLE_HOME_PATH,
  STAFF_HOME_PATH,
  type StaffRole,
} from '@therapy/shared-types';
import axios from 'axios';
import { makeAutoObservable, runInAction } from 'mobx';

import { queryClient } from '../query-client.js';

const API_BASE = '/api/v1';

interface DecodedToken {
  readonly type: 'customer' | 'staff' | 'impersonation';
  readonly roles?: readonly CustomerRole[];
  readonly productAdminRoles?: readonly StaffRole[];
  readonly clinicId?: string;
  readonly impersonatingClinicId?: string;
  readonly modAccess?: Readonly<Record<string, number>>;
  readonly exp: number;
}

interface LoginPayload {
  readonly email: string;
  readonly password: string;
  readonly clinicId?: string | undefined;
  readonly totp?: string | undefined;
  readonly rememberMe?: boolean | undefined;
}

interface LoginResponseBody {
  readonly accessToken?: string;
  readonly requiresClinicSelection?: boolean;
  readonly clinics?: readonly { id: string; name: string }[];
  readonly mfaRequired?: boolean;
}

/** Outcome of a customer sign-in attempt — mirrors the API's three branches. */
export type CustomerSignInResult =
  | { readonly kind: 'success' }
  | { readonly kind: 'clinic-selection'; readonly clinics: readonly { id: string; name: string }[] }
  | { readonly kind: 'mfa-required' };

/** A 401 from the customer surface means "try the staff surface next". */
function isUnauthorized(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 401;
}

/** Decode the JWT payload without verifying — verification is the server's job. */
function decodeJwt(token: string): DecodedToken | null {
  const segments = token.split('.');
  const payload = segments[1];
  if (payload === undefined) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as DecodedToken;
  } catch {
    return null;
  }
}

/**
 * Single-flight guard for `refresh()`. The refresh token is single-use and
 * rotated server-side, so two concurrent `/auth/refresh` calls (e.g. React
 * StrictMode double-invoking the guard effect in dev, or several guards mounting
 * at once) would make the second reuse an already-consumed token → 401. Sharing
 * one in-flight promise collapses them into a single network call.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Authentication state for both surfaces (customer + staff). Access token is
 * kept in memory only — never localStorage — per iTech §5.1. The httpOnly
 * refresh cookie lets a page reload re-establish the session via `refresh()`.
 */
export class AuthStore {
  public accessToken: string | null = null;
  public initializing = true;

  public constructor() {
    makeAutoObservable(this);
  }

  public get claims(): DecodedToken | null {
    return this.accessToken === null ? null : decodeJwt(this.accessToken);
  }

  public get isAuthenticated(): boolean {
    const claims = this.claims;
    return claims !== null && claims.exp * 1000 > Date.now();
  }

  public get isStaff(): boolean {
    return this.claims?.type === 'staff';
  }

  public get isImpersonating(): boolean {
    return this.claims?.type === 'impersonation';
  }

  /** Customer roles on the active session (empty for staff). */
  public get roles(): readonly CustomerRole[] {
    const claims = this.claims;
    return claims !== null && claims.type !== 'staff' ? (claims.roles ?? []) : [];
  }

  /** Per-module RBAC access level (custom-role gating); empty for system roles. */
  public get modAccess(): Readonly<Record<string, number>> {
    return this.claims?.modAccess ?? {};
  }

  /** True when the session has only custom (non-system) roles → nav is gated. */
  public get isCustomRoleOnly(): boolean {
    const r = this.roles;
    return r.length > 0 && !r.some((role) => (CUSTOMER_ROLES as readonly string[]).includes(role));
  }

  /**
   * Nav-gating: system roles keep their full portal nav; custom roles see only
   * modules granted a non-zero access level. Unmapped modules stay visible.
   */
  public canSeeModule(module: string | undefined): boolean {
    if (module === undefined || !this.isCustomRoleOnly) {
      return true;
    }
    return (this.modAccess[module] ?? 0) > 0;
  }

  /** Path the current session should land on after login. */
  public get homePath(): string {
    const claims = this.claims;
    if (claims === null) return '/';
    if (claims.type === 'staff') return STAFF_HOME_PATH;
    const role = claims.roles?.[0];
    if (role !== undefined && (CUSTOMER_ROLES as readonly string[]).includes(role)) {
      return ROLE_HOME_PATH[role];
    }
    // Custom-role-only users are clinic staff → land on the admin portal (nav-gated).
    return role !== undefined ? '/admin' : '/';
  }

  public async signInCustomer(payload: LoginPayload): Promise<CustomerSignInResult> {
    const { data } = await axios.post<LoginResponseBody>(`${API_BASE}/auth/login`, payload, {
      withCredentials: true,
    });

    if (data.requiresClinicSelection === true) {
      return { kind: 'clinic-selection', clinics: data.clinics ?? [] };
    }
    if (data.mfaRequired === true) {
      return { kind: 'mfa-required' };
    }
    if (data.accessToken === undefined) {
      throw new Error('Malformed login response');
    }
    const token = data.accessToken;
    runInAction(() => {
      this.accessToken = token;
    });
    return { kind: 'success' };
  }

  public async signInStaff(payload: {
    readonly email: string;
    readonly password: string;
    readonly totp?: string | undefined;
  }): Promise<void> {
    const body =
      payload.totp !== undefined && payload.totp !== ''
        ? { email: payload.email, password: payload.password, totp: payload.totp }
        : { email: payload.email, password: payload.password };
    const { data } = await axios.post<{ accessToken: string }>(
      `${API_BASE}/auth/staff/login`,
      body,
      { withCredentials: true },
    );
    runInAction(() => {
      this.accessToken = data.accessToken;
    });
  }

  /**
   * Unified sign-in for the single login screen. Tries the customer surface
   * first; a 401 (e.g. the email belongs to a staff/Super Admin account) falls
   * back to the staff surface. The two token types stay distinct under the hood;
   * `homePath` then routes to the right portal (incl. `/sa` for staff).
   */
  public async signIn(payload: LoginPayload): Promise<CustomerSignInResult> {
    try {
      return await this.signInCustomer(payload);
    } catch (err) {
      if (!isUnauthorized(err)) {
        throw err;
      }
      await this.signInStaff({
        email: payload.email,
        password: payload.password,
        totp: payload.totp,
      });
      return { kind: 'success' };
    }
  }

  /**
   * Re-establish a session from the httpOnly refresh cookie. Single-flighted so
   * concurrent callers (StrictMode double-mount, multiple guards, the apiClient
   * 401 interceptor) share one rotation and never reuse a consumed token.
   */
  public async refresh(): Promise<boolean> {
    refreshInFlight ??= this.doRefresh().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const { data } = await axios.post<{ accessToken: string }>(
        `${API_BASE}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      runInAction(() => {
        this.accessToken = data.accessToken;
        this.initializing = false;
      });
      return true;
    } catch {
      runInAction(() => {
        this.accessToken = null;
        this.initializing = false;
      });
      return false;
    }
  }

  public signOut(): void {
    this.accessToken = null;
    // Drop every cached query (profile, dashboards, lists, …) so the next
    // sign-in — even to a different account — never briefly renders this
    // session's stale data before its own queries refetch.
    queryClient.clear();
    // Fire-and-forget server-side revocation; UI does not wait on it.
    void axios
      .post(`${API_BASE}/auth/logout`, {}, { withCredentials: true })
      .catch(() => undefined);
  }
}

export const authStore = new AuthStore();
